import type { ItemFieldRow } from './item-field-reader';
import { isOwnedRuleWindow } from './owned-rule-window';
import { readItemField } from './item-field-reader';
import { createFormulaReadContext } from './formula-read-context';
import type { PcPreliminaryEssenceDependencies } from './preliminary-essence';
import type { ProviderCatalogRows } from './provider-catalog-order';
import type { FromBaseInventoryTree } from './from-base-inventory';
import staticData from './from-base-static-registries.json';
import { plainTextDecode } from './possessor-compile';
import { phpJsonString } from './php-json-values';
import { TINY_CREATURE_SIZE_ID } from './creature-size';
import { phpJsonTruthy } from './php-json-values';
import type { PlSignatureMetadata } from './pl-signature';
import { memoFromBaseRead, withFromBaseReadMemo } from './from-base-read-memo';
export { memoFromBaseRead } from './from-base-read-memo';

export type FromBaseRow = Record<string, unknown>;
export interface FromBasePlayerInput extends Pick<FromBaseInventoryTree,
    'inventory_order' | 'picture_sources' | 'picture_lookup_ids' | 'definition_videos' | 'inventory_metadata'> {
    entity_id: string;
    base_rev: number;
    player_characters: FromBaseRow;
    catalog: Readonly<Record<string, FromBaseRow>> | readonly [];
    user_choices: readonly FromBaseRow[];
    encounter_creatures_spells_prepared?: readonly FromBaseRow[];
    encounter_creatures?: FromBaseRow;
    caster_summons?: readonly FromBaseRow[];
    inventory?: readonly FromBaseRow[];
    pl_signature?: PlSignatureMetadata;
    random_table_rows?: Readonly<Record<string, readonly FromBaseRow[] | null>>;
    inventory_encounter_links?: readonly FromBaseRow[];
    statuses?: FromBaseRow;
    charges?: readonly FromBaseRow[];
    pictures?: readonly FromBaseRow[];
    meshes?: readonly FromBaseRow[];
    avatar_wardrobe?: FromBaseRow;
    encounter_context?: FromBaseRow;
    status_owners?: readonly FromBaseRow[];
    status_target_references?: { inventory: readonly FromBaseRow[]; encounter_creatures: readonly FromBaseRow[] };
    status_connection_rows?: readonly FromBaseRow[];
    status_origin_references?: FromBaseRow;
    status_origin_inventory_sources?: readonly (FromBaseInventoryTree & { catalog?: Readonly<Record<string, FromBaseRow>> })[];
    enchantment_templates?: readonly FromBaseRow[];
    enchantment_template_enchantments?: readonly FromBaseRow[];
    monster_templates?: readonly FromBaseRow[];
    monster_template_layers?: readonly FromBaseRow[];
    missing_enchantment_template_ids?: readonly string[];
    missing_monster_template_ids?: readonly string[];
    /** Explicit missing authored references; deleted source rows remain readable like native compilation. */
    missing_definition_ids?: readonly string[];
}
export interface FromBasePlayerMetadata {
    /** Request-owned constructor for fully compiled authored providers. */
    definitionLookup?: (id: string) => FromBaseRow | null;
    layerCatalog: ProviderCatalogRows & Readonly<Record<string, {
        aggregation_strategy?: string;
        is_multiple?: boolean;
    }>>;
    abilityTypes: readonly string[];
    additionalAbilityTypes: readonly { add_ability_name: string; ability_name: string }[];
    preliminary: PcPreliminaryEssenceDependencies['metadata'];
    /** Native merge_dynamic_classification eligibility, supplied by frontend metadata. */
    classificationSizeTypes?: readonly string[];
    cdnUrl?: string;
}
export class FromBaseInputMissing extends Error {
    constructor(readonly dependency: string, readonly id: string) {
        super(`from-base-input-missing:${dependency}:${id}`);
    }
}
export class FromBaseStageMissing extends Error {
    constructor(readonly stage: string, readonly detail: string) {
        super(`from-base-stage-missing:${stage}:${detail}`);
    }
}
export const fromBaseUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value);
export const fromBaseRecord = (value: unknown): value is FromBaseRow => value !== null
    && typeof value === 'object' && !Array.isArray(value);
let sharingDefinitions = false;
/** Compiler stages own their mutable host branches; frozen definitions stay
 * shared. A producer cloning a frozen root still receives a fully owned tree. */
export function withFromBaseReadSharing<T>(run: () => T): T {
    const previous = sharingDefinitions; sharingDefinitions = true;
    try { return run(); } finally { sharingDefinitions = previous; }
}
/** Compiler trees are native JSON. Clone directly without the structured-clone
 * wire serializer at every stage; preserve aliases/cycles and native fallbacks. */
export function fromBaseClone<T>(value: T, sharedKeys?: ReadonlySet<string>, ownFrozenChildren = false, omitKeys?: ReadonlySet<string>,
    onClone?: (value: object) => void, observationSkipKeys?: ReadonlySet<string>): T {
    if (isOwnedRuleWindow(value)) return value;
    if (value === null || (typeof value !== 'object' && typeof value !== 'function' && typeof value !== 'symbol')) return value;
    const copies = new WeakMap<object, unknown>();
    const observed = onClone ? new WeakSet<object>() : undefined;
    // An alias first copied under a skipped key can become observable later.
    const observeCopy = (copy: object): void => {
        if (!onClone || observed!.has(copy)) return;
        observed!.add(copy);
        for (const key of Object.keys(copy)) {
            if (observationSkipKeys?.has(key)) continue;
            const child = (copy as Record<string, unknown>)[key];
            if (child !== null && typeof child === 'object') observeCopy(child);
        }
        onClone(copy);
    };
    const shareChildren = sharingDefinitions && !ownFrozenChildren && value !== null && typeof value === 'object' && !Object.isFrozen(value);
    const clone = (input: unknown, observe = onClone !== undefined): unknown => {
        if (input === null || typeof input !== 'object') {
            if (typeof input === 'function' || typeof input === 'symbol') return structuredClone(input);
            return input;
        }
        if (copies.has(input)) {
            const copy = copies.get(input) as object;
            if (observe) observeCopy(copy);
            return copy;
        }
        if (shareChildren && Object.isFrozen(input)) return input;
        if (!Array.isArray(input)) {
            const prototype = Object.getPrototypeOf(input);
            if (prototype !== Object.prototype && prototype !== null) return structuredClone(input);
        }
        const output: Record<string, unknown> = Array.isArray(input) ? new Array(input.length) as unknown as Record<string, unknown> : {};
        copies.set(input, output);
        for (const key of Object.keys(input)) {
            if (omitKeys?.has(key)) continue;
            const child = (input as Record<string, unknown>)[key];
            const value = sharedKeys?.has(key) ? child : clone(child, observe && !observationSkipKeys?.has(key));
            if (key === '__proto__') Object.defineProperty(output, key, { value, enumerable: true, writable: true, configurable: true });
            else output[key] = value;
        }
        if (observe && onClone) { observed!.add(output); onClone(output); }
        return output;
    };
    return clone(value) as T;
}

/** Definitions are shared read values. Producers copy the branches they change. */
export function freezeFromBaseRow<T>(value: T): T {
    if (value === null || typeof value !== 'object' || Object.isFrozen(value)) return value;
    for (const child of Object.values(value)) freezeFromBaseRow(child);
    return Object.freeze(value);
}

let validation: WeakSet<object> | undefined;
/** NPC windows add the same registry definitions to the same raw catalog. */
export function fromBaseCatalogWithSources(catalog: FromBaseRow, sources?: Readonly<Record<string, FromBaseRow>>): FromBaseRow {
    if (!sources) return catalog;
    const merges = memoFromBaseRead(catalog, 'stage-source-catalogs', () => new WeakMap<object, FromBaseRow>());
    let merged = merges.get(sources);
    if (!merged) {
        merged = { ...sources, ...catalog }; merges.set(sources, merged);
        memoFromBaseRead(merged, 'stage-source-catalogs', () => new WeakMap<object, FromBaseRow>()).set(sources, merged);
    }
    return merged;
}
/** A compile walks each received saved/metadata object once, including nested producers. */
export function withFromBaseValidation<T>(run: () => T, seen = validation ?? new WeakSet<object>()): T {
    const previous = validation;
    validation = seen;
    try { return withFromBaseReadMemo(seen, run); } finally { validation = previous; }
}

/** BASE cannot contain an already compiled subtree, even in a referenced definition. */
export function assertUncomputedFromBase(value: unknown, path = 'base'): void {
    const seen = validation ?? new WeakSet<object>();
    const visit = (node: unknown, at: string): void => {
        if (node === null || typeof node !== 'object' || seen.has(node)) return;
        seen.add(node);
        for (const key of Object.keys(node)) {
            if (key === 'computed') throw new TypeError(`from-base-computed-input:${at}.computed`);
            const child = (node as FromBaseRow)[key];
            if (child !== null && typeof child === 'object') visit(child, `${at}.${key}`);
        }
    };
    visit(value, path);
}

/** Request-local raw definition adapters. Inventory construction is deliberately unavailable here. */
export function createFromBasePlayerContext(base: FromBasePlayerInput, metadata: FromBasePlayerMetadata) {
    assertUncomputedFromBase(base);
    assertUncomputedFromBase(metadata, 'metadata');
    if (!fromBaseUuid(base.entity_id) || !Number.isSafeInteger(base.base_rev) || base.base_rev < 0
        || !fromBaseRecord(base.player_characters) || !fromBaseUuid(base.player_characters.item_id)
        || !(fromBaseRecord(base.catalog) || (Array.isArray(base.catalog) && base.catalog.length === 0)) || !Array.isArray(base.user_choices)) {
        throw new TypeError('Invalid raw PC base envelope');
    }
    const catalog = memoFromBaseRead(base.catalog, 'player-catalog-index', () => {
        const catalog = new Map<string, FromBaseRow>();
        for (const [key, definition] of Object.entries(base.catalog)) {
            if (!fromBaseUuid(key) || !fromBaseRecord(definition) || !fromBaseRecord(definition.items)
                || !(fromBaseRecord(definition.item_layer_types) || (Array.isArray(definition.item_layer_types) && definition.item_layer_types.length === 0)) || !fromBaseUuid(definition.items.item_id)
                || definition.items.item_id.toUpperCase() !== key.toUpperCase()) {
                throw new TypeError(`Invalid authored definition: ${key}`);
            }
            if (catalog.has(key.toUpperCase())) throw new TypeError(`Duplicate authored definition: ${key}`);
            catalog.set(key.toUpperCase(), definition);
        }
        return catalog;
    });
    const missing = new Set((base.missing_definition_ids ?? []).map(id => {
        if (!fromBaseUuid(id) || catalog.has(id.toUpperCase())) throw new TypeError(`Invalid known definition miss: ${id}`);
        return id.toUpperCase();
    }));
    const preparingSize = new Set<string>();
    function rawDefinition(id: string): FromBaseRow | null {
        const row = catalog.get(id.toUpperCase());
        if (row) return row;
        if (missing.has(id.toUpperCase())) return null;
        throw new FromBaseInputMissing('definition', id);
    }
    function definition(id: string): ItemFieldRow | null {
        if (!fromBaseUuid(id)) throw new TypeError(`Invalid raw definition reference: ${id}`);
        if (metadata.definitionLookup) return metadata.definitionLookup(id);
        const row = catalog.get(id.toUpperCase());
        if (row) {
            const out = { ...row }, key = id.toUpperCase();
            // C1 mirrors are normalized only for the definition being read.
            // Identity reads do not copy the entire authored/media graph.
            const layers = { ...out.item_layer_types as FromBaseRow };
            out.item_layer_types = layers;
            for (const name of Object.keys(layers)) delete out[name];
            const type = readers.itemField(out, 'item_type');
            // merge_dynamic_classification calls ft_apply_object_vitality for
            // these definition types before the PC ever consumes their size.
            if (metadata.classificationSizeTypes?.includes(String(type)) && !preparingSize.has(key)) {
                preparingSize.add(key);
                try {
                    if (!phpJsonTruthy(readers.layerValue(out, 'size')))
                        (out.item_layer_types as FromBaseRow).size = TINY_CREATURE_SIZE_ID;
                } finally { preparingSize.delete(key); }
            }
            return out;
        }
        if (missing.has(id.toUpperCase())) return null;
        throw new FromBaseInputMissing('definition', id);
    }
    const unavailableInventory = (id: string): never => { throw new FromBaseStageMissing('inventory-construction', id); };
    const dependencies: PcPreliminaryEssenceDependencies = {
        metadata: metadata.preliminary,
        catalogRows: metadata.layerCatalog,
        abilityTypes: metadata.abilityTypes,
        aggregationStrategy: name => {
            const row = metadata.layerCatalog[name];
            // ItemLayerTypeCatalog::aggregationStrategy uses this default for
            // virtual/retired layer names too (e.g. the curse ability bucket).
            return row?.aggregation_strategy ?? 'host_wins';
        },
        resolveCarrierRef: definition,
        queryFullItem: definition,
        resolveInventoryInstance: unavailableInventory,
        resolveInventoryIdentity: unavailableInventory,
        resolveItemName: id => {
            const named = (staticData.names as Readonly<Record<string, string>>)[id];
            if (named) return named;
            const row = rawDefinition(id);
            return row && fromBaseRecord((row as FromBaseRow).items) ? plainTextDecode(phpJsonString(readItemField(row, 'item_name') ?? '')) ?? '' : '';
        },
    };
    const readers = createFormulaReadContext(dependencies);
    return { definition, rawDefinition, readers, dependencies };
}
